/**
 * features/finance/FullAnnualPayment.tsx
 * ------------------------------------------------------------------
 * Families paying the whole year up front.
 *
 * A finance manager proposes a family for a given year; the Head approves.
 * Only once approved does the billing run treat them as prepaying — the
 * prepayment discount applies and the year bills as a single up-front
 * invoice instead of three termly ones.
 *
 * TWO WAYS A FAMILY CAN BE PREPAYING, and this screen shows both.
 *
 * The long-standing route is `family.billingPlan.kind === 'prepayYear'`, set
 * on the Families screen and honoured by the billing run since the fee module
 * was written. This screen adds a second, per-year, approved route. The
 * billing run reads the UNION of the two, so nothing recorded the old way is
 * dropped — but a bursar looking at this list needs to know that a family
 * already on the old plan is prepaying whether or not it appears here as a
 * case. Those families are shown, labelled, and cannot be double-flagged into
 * confusion.
 * ------------------------------------------------------------------ */
import { useState, useMemo } from 'react';
import { Search, X, Plus, CalendarCheck, Info } from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { auditFromContext } from '@/lib/audit';
import { Spinner } from '@/components/ui/primitives';
import { SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';
import { finance } from '@/features/finance/financeWrite';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { feeCaseApproved, type PrepayFamily } from '@/lib/financeCore';
import { FeeCaseBadge, FeeCaseDecision, approvalPatch } from '@/features/finance/FeeCaseApproval';

const CURRENT_YEAR = String(new Date().getFullYear());

export default function FullAnnualPayment() {
  const { families, activeStudents, loading } = useData();
  const { settings, prepayFamilies } = useFinanceData();
  const { userData, userRoles } = useAuth();
  const perms = getPermissions(userRoles);

  const [year, setYear] = useState(CURRENT_YEAR);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const docId = (familyId: string) => `${familyId}_${year}`;
  const caseByDoc = useMemo(
    () => Object.fromEntries(prepayFamilies.map((r) => [r.id, r])) as Record<string, PrepayFamily>,
    [prepayFamilies],
  );

  /** Families already prepaying via the older per-family billing plan. */
  const legacyPrepay = useMemo(
    () => new Set(families.filter((f) => f.billingPlan?.kind === 'prepayYear').map((f) => f.id)),
    [families],
  );

  const childCount = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of activeStudents) if (s.familyId) m[s.familyId] = (m[s.familyId] ?? 0) + 1;
    return m;
  }, [activeStudents]);

  const familyName = useMemo(() => new Map(families.map((f) => [f.id, f.name])), [families]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return families
      .filter((f) => (f.name || '').toLowerCase().includes(q))
      .map((f) => ({ f, kase: caseByDoc[`${f.id}_${year}`], legacy: legacyPrepay.has(f.id) }))
      .sort((a, b) => {
        // Anything needing a decision floats to the top; then the settled
        // cases; then everyone else alphabetically.
        const rank = (r: typeof a) => (r.kase && !feeCaseApproved(r.kase) && r.kase.status !== 'rejected' ? 0 : r.kase || r.legacy ? 1 : 2);
        return rank(a) - rank(b) || (a.f.name || '').localeCompare(b.f.name || '');
      });
  }, [families, search, caseByDoc, legacyPrepay, year]);

  const approvedCount = rows.filter((r) => feeCaseApproved(r.kase)).length;
  const pendingCount = rows.filter((r) => r.kase && (r.kase.status ?? 'pending') === 'pending').length;
  const effectiveCount = rows.filter((r) => feeCaseApproved(r.kase) || r.legacy).length;

  async function propose(familyId: string) {
    if (busy) return;
    setBusy(true);
    try {
      await finance.prepayFamilies.set(docId(familyId), {
        id: docId(familyId), familyId, year,
        status: 'pending',
        createdBy: userData?.uid, createdByName: userData?.displayName,
      });
      auditFromContext(userData, 'prepay_family_proposed', 'finance', {
        targetId: familyId, targetType: 'setting', targetName: familyName.get(familyId),
        details: { year, familyId },
      });
    } finally { setBusy(false); }
  }

  async function decide(familyId: string, decision: 'approved' | 'rejected', reason?: string) {
    if (busy) return;
    setBusy(true);
    try {
      await finance.prepayFamilies.update(docId(familyId), approvalPatch(decision, userData, reason));
      auditFromContext(userData, `prepay_family_${decision}`, 'finance', {
        targetId: familyId, targetType: 'setting', targetName: familyName.get(familyId),
        details: { year, familyId, reason },
      });
    } finally { setBusy(false); }
  }

  async function remove(familyId: string) {
    if (busy) return;
    setBusy(true);
    try {
      await finance.prepayFamilies.delete(docId(familyId));
      auditFromContext(userData, 'prepay_family_removed', 'finance', {
        targetId: familyId, targetType: 'setting', targetName: familyName.get(familyId),
        details: { year, familyId },
      });
    } finally { setBusy(false); }
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner label="Loading families…" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Full annual payment</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Families paying the year up front. Once approved, the prepayment discount
            ({settings.prepaymentDiscountPercent || 0}%) applies and the year bills as one invoice.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm shrink-0">
          <span className="text-gray-500 dark:text-gray-400 text-xs">Year</span>
          <select value={year} onChange={(e) => setYear(e.target.value)} className={glassFieldClass('py-1.5 w-28 text-sm')}>
            {[0, 1, -1].map((d) => String(Number(CURRENT_YEAR) + d)).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatChip tone="emerald" value={String(approvedCount)} label={`Approved for ${year}`} icon={CalendarCheck} />
        <StatChip tone="warn" value={String(pendingCount)} label="Awaiting approval" />
        <StatChip tone="navy" value={String(effectiveCount)} label="Prepaying in the next run" />
      </div>

      {effectiveCount !== approvedCount && (
        <SoftCard pad="p-3">
          <p className="text-xs text-gray-600 dark:text-gray-300 flex items-start gap-2">
            <Info size={14} className="mt-0.5 shrink-0 text-primary dark:text-primarySoft" />
            <span>
              {effectiveCount - approvedCount} famil{effectiveCount - approvedCount === 1 ? 'y is' : 'ies are'} prepaying
              through the older per-family billing plan set on the Families screen, without a case here. They are
              billed as prepaying either way — the billing run reads both.
            </span>
          </p>
        </SoftCard>
      )}

      <SoftCard pad="p-3">
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search families…"
            className={glassFieldClass('pl-9')}
          />
        </div>
      </SoftCard>

      <SoftCard pad="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2.5 font-medium">Family</th>
                <th className="px-4 py-2.5 font-medium text-center">Children</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.slice(0, 300).map(({ f, kase, legacy }) => (
                <tr key={f.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                  <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-100">
                    {f.name}
                    {kase?.createdByName && (
                      <span className="text-[11px] text-gray-400 ml-2">proposed by {kase.createdByName}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center text-gray-600 dark:text-gray-300">{childCount[f.id] ?? 0}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {kase
                        ? <FeeCaseBadge status={kase.status} approvedByName={kase.approvedByName} rejectedReason={kase.rejectedReason} />
                        : <span className="text-gray-400 text-xs">—</span>}
                      {legacy && (
                        <span
                          title="Set on the Families screen as a per-family billing plan. Honoured by the billing run."
                          className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                        >
                          billing plan
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      {kase && perms.canApproveFeeCases && (
                        <FeeCaseDecision
                          status={kase.status}
                          busy={busy}
                          onApprove={() => decide(f.id, 'approved')}
                          onReject={(reason) => decide(f.id, 'rejected', reason)}
                        />
                      )}
                      {!kase && perms.canManageFinanceConfig && (
                        <button
                          type="button"
                          onClick={() => propose(f.id)}
                          disabled={busy}
                          className="px-2.5 py-1 rounded-lg text-xs font-semibold text-primary dark:text-primarySoft border-2 border-primary/30 hover:bg-primary/5 disabled:opacity-40 inline-flex items-center gap-1"
                        >
                          <Plus size={12} /> Propose
                        </button>
                      )}
                      {kase && perms.canManageFinanceConfig && (
                        <button
                          type="button"
                          onClick={() => remove(f.id)}
                          disabled={busy}
                          className="p-1 rounded text-gray-400 hover:text-red-600 disabled:opacity-40"
                          title="Remove this case"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > 300 && (
          <p className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-100 dark:border-gray-800">
            Showing the first 300 of {rows.length}. Narrow the search to see the rest.
          </p>
        )}
      </SoftCard>
    </div>
  );
}
